import { DeviceAuthAlertMessage } from '@/app/components'
import type { Vehicle } from '@/types'
import { match } from 'ts-pattern'
import { useUtilityProgramsWithVehicleEnrollment } from './TeslaFleetAlertState'
import React from 'react'

type DeviceAlertModes = 'error' | 'warning' | 'info'

export const isVehicleEligibleForFleetMigration = (vehicle: Vehicle) => {
  return (
    (vehicle.car.access_type === 'OWNER' &&
      vehicle.car.supports_command_signing) ??
    false
  )
}

function useViewModel({ vehicle }: { vehicle: Vehicle }) {
  const utilityProgramsWithVehicleEnrolled =
    useUtilityProgramsWithVehicleEnrollment(vehicle)

  const isVehicleEnrolled = utilityProgramsWithVehicleEnrolled.length > 0
  const isEligibleForFleetMigration =
    isVehicleEligibleForFleetMigration(vehicle)

  return {
    isVehicleEnrolled,
    isEligibleForFleetMigration,
  }
}

export function getTeslaAlertMode(
  isVehicleEnrolled: boolean,
  commandProtocolDisabled: boolean,
): DeviceAlertModes {
  return match({
    isVehicleEnrolled,
    commandProtocolDisabled,
  })
    .with({ commandProtocolDisabled: true }, () => 'error' as DeviceAlertModes)
    .with({ isVehicleEnrolled: true }, () => 'error' as DeviceAlertModes)
    .with({ isVehicleEnrolled: false }, () => 'warning' as DeviceAlertModes)
    .exhaustive()
}

export function getTeslaAlertBanner(
  mode: DeviceAlertModes,
  isVehicleEnrolled: boolean,
  isEligibleForFleetMigration: boolean,
): React.ReactElement {
  return match({ isVehicleEnrolled, isEligibleForFleetMigration })
    .with(
      { isVehicleEnrolled: true, isEligibleForFleetMigration: true },
      () => <EnrolledAndEligibleVehicleBanner mode={mode} />,
    )
    .with(
      { isVehicleEnrolled: true, isEligibleForFleetMigration: false },
      () => <EnrolledAndNotEligibleVehicleBanner mode={mode} />,
    )
    .with(
      { isVehicleEnrolled: false, isEligibleForFleetMigration: true },
      () => <NotEnrolledAndEligibleVehicleBanner mode={mode} />,
    )
    .with(
      { isVehicleEnrolled: false, isEligibleForFleetMigration: false },
      () => <NotEnrolledAndNotEligibleVehicleBanner mode={mode} />,
    )
    .exhaustive()
}

export default function getFleetMigrationBanner({
  vehicle,
}: {
  vehicle: Vehicle
}): {
  id: number
  mode: 'error' | 'warning' | 'info'
  renderBanner: () => React.ReactNode
} {
  const { isVehicleEnrolled, isEligibleForFleetMigration } = useViewModel({
    vehicle,
  })

  const commandProtocolDisabled = Boolean(
    vehicle.car?.tesla_command_protocol_disabled,
  )

  const mode = getTeslaAlertMode(isVehicleEnrolled, commandProtocolDisabled)

  const banner = getTeslaAlertBanner(
    mode,
    isVehicleEnrolled,
    isEligibleForFleetMigration,
  )

  return {
    id: vehicle.id,
    mode,
    renderBanner: () => banner,
  } as const
}

export function EnrolledAndEligibleVehicleBanner({
  mode,
}: {
  mode: DeviceAlertModes
}) {
  return (
    <DeviceAuthAlertMessage
      mode={mode}
      deviceName="vehicle"
      linkText="Upgrade Your Connection Now"
      reconnectUrl={'/connect-device/vehicle/tesla/permissions-statement'}
      show
    >
      You're about to lose your program rewards because Tesla will soon stop
      supporting the way you’ve connected your car to Optiwatt.
    </DeviceAuthAlertMessage>
  )
}

export function EnrolledAndNotEligibleVehicleBanner({
  mode,
}: {
  mode: DeviceAlertModes
}) {
  return (
    <DeviceAuthAlertMessage
      mode={mode}
      deviceName="vehicle"
      show
      linkText="Upgrade Your Connection Now"
      reconnectUrl={'/connect-device/vehicle/tesla/permissions-statement'}
    >
      You’re about to lose your program rewards because Tesla will soon stop
      supporting the way you’ve connected your car to Optiwatt.
    </DeviceAuthAlertMessage>
  )
}

export function NotEnrolledAndEligibleVehicleBanner({
  mode,
}: {
  mode: DeviceAlertModes
}) {
  return (
    <DeviceAuthAlertMessage
      mode={mode}
      deviceName="vehicle"
      linkText="Upgrade Your Connection Now"
      reconnectUrl={'/connect-device/vehicle/tesla/permissions-statement'}
      show
    >
      Tesla will soon stop supporting the way you’ve connected your car to
      Optiwatt. When that happens, you’ll lose the ability for Optiwatt to
      optimize your charging.
    </DeviceAuthAlertMessage>
  )
}

export function NotEnrolledAndNotEligibleVehicleBanner({
  mode,
}: {
  mode: DeviceAlertModes
}) {
  return (
    <DeviceAuthAlertMessage
      mode={mode}
      deviceName="vehicle"
      show
      linkText="Upgrade Your Connection Now"
      reconnectUrl={'/connect-device/vehicle/tesla/permissions-statement'}
    >
      Tesla will soon stop supporting the way you’ve connected your car to
      Optiwatt.
    </DeviceAuthAlertMessage>
  )
}
